import React, { useEffect, useRef, useState } from "react";
import { GridPaginationModel, useGridApiRef } from "@mui/x-data-grid-pro";
import ManageColumnsButton from "../../components/ManageColumnsButton";
import SystemExportButton from "../../components/SystemExportButton";
import {addToUrlParams} from "@dreebit-registry/vsm-microservice-components";
import _ from "lodash";
import ListWithFilterDrawer from "../../components/ListWithFilterDrawer";
import { useSyncedListParams } from "../../hooks/useSyncedListParams";
import SystemsTable from "../../components/SystemsTable";
import { SearchSystemTdas } from "../../graphql/queries/searchSystemTdas";
import { TypeDefinitionAttribute } from "../../types";

interface Props {
    onOpenSystem: (systemId: string) => void;
}

const SystemListScreen = ({ onOpenSystem }: Props) => {
    const apiRef = useGridApiRef();
    const manageColumnsButtonRef = useRef<HTMLButtonElement>(null);
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    // The export button sits in the header, outside the drawer's attribute injection
    const [attributes, setAttributes] = useState<TypeDefinitionAttribute[]>([]);

    const showEnvBanner = process.env.REACT_APP_SHOW_TESTING_BANNER === "true";
    const tableHeight =
        viewportHeight -
        (showEnvBanner ? 40 : 0) - // Test Env Banner
        56 - // Filter SearchBar
        63 - // header
        50 - // Tabs
        16; // content padding

    const {
        urlParams,
        setUrlParams,
        searchParams,
        setSearchParams,
        filterParams,
        setFilterParams,
        resetFilterParams,
    } = useSyncedListParams({
        storagePrefix: "systemList",
        defaultQueryFields: ["SYSTEM_TITLE", "IOT_GATEWAY_IMEI"],
    });

    const onDebouncedSearch = _.debounce((values) => {
        setSearchParams(values);
        addToUrlParams(urlParams, values, setUrlParams);
    }, 700);

    useEffect(() => {
        const handleResize = () => {
            setViewportHeight(window.innerHeight);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <ListWithFilterDrawer
            query={SearchSystemTdas}
            queryVariables={{ typeDefinitionIndex: "DEFAULT" }}
            attributesPath="searchSystems.filterTypeDefinitionAttributeList.typeDefinitionAttributes"
            urlParams={urlParams}
            setUrlParams={setUrlParams}
            searchParams={searchParams}
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            resetFilterParams={resetFilterParams}
            onSearch={onDebouncedSearch}
            openStateStorageKey="systemList_filterDrawerOpen"
            onAttributesChange={setAttributes}
            extraHeaderActions={
                <>
                    <ManageColumnsButton apiRef={apiRef} buttonRef={manageColumnsButtonRef}/>
                    <SystemExportButton
                        filter={filterParams}
                        searchParams={searchParams}
                        attributes={attributes}
                        apiRef={apiRef}
                    />
                </>
            }
        >
            <SystemsTable
                apiRef={apiRef}
                panelAnchorRef={manageColumnsButtonRef}
                height={tableHeight}
                searchParams={searchParams}
                urlParams={urlParams}
                filter={filterParams}
                onChangePagination={(newPaginationModel: GridPaginationModel) => {
                    if (newPaginationModel) {
                        addToUrlParams(urlParams, newPaginationModel, setUrlParams);
                    }
                }}
                onOpenSystem={onOpenSystem}
            />
        </ListWithFilterDrawer>
    );
};

export default SystemListScreen;
